import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const DeliveryTime = ({
  selectedDay,
  setSelectedDay,
  selectedTimeSlot,
  setSelectedTimeSlot,
  expressDelivery,
  setExpressDelivery,
  selfPickup,
  setSelfPickup,
}) => {
  const timeSlots = ['10:00-12:00', '12:00-14:00', '14:00-16:00', '16:00-18:00', '18:00-20:00'];

  const [availableSlots, setAvailableSlots] = useState([]);

  useEffect(() => {
    // Get available time slots based on current time
    const slots = getAvailableTimeSlots(selectedDay);
    setAvailableSlots(slots);

    // Select first available slot if current selection is no longer available
    if (slots.length > 0) {
      if (!selectedTimeSlot || !slots.includes(selectedTimeSlot)) {
        setSelectedTimeSlot(slots[0]);
      }
    } else if (selectedDay === 'today') {
      // If no slots available today, switch to tomorrow
      setSelectedDay('tomorrow');
    }
  }, [selectedDay]);

  const getAvailableTimeSlots = (day) => {
    return timeSlots.filter((slot) => {
      if (day === 'tomorrow') {
        return true;
      } else {
        return isSlotAvailable(slot);
      }
    });
  };

  const isSlotAvailable = (slot) => {
    const currentTime = new Date();
    const [start] = slot.split('-');
    const [startHours, startMinutes] = start.split(':');
    const slotStartTime = new Date();
    slotStartTime.setHours(Number.parseInt(startHours), Number.parseInt(startMinutes), 0, 0);

    return slotStartTime > currentTime;
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const date = new Date(0, 0, 0, Number(hours), Number(minutes));
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <motion.div
      className="p-4 border-b bg-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
    >
      <h2 className="font-semibold mb-3 text-gray-800">Delivery Time</h2>

      <div className="space-y-4">
        {/* Delivery Options */}
        <div className="flex justify-between">
          <div className="flex items-center">
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input
                type="checkbox"
                id="expressDelivery"
                checked={expressDelivery}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelfPickup(false);
                  }
                  setExpressDelivery(e.target.checked);
                }}
                disabled={new Date().getHours() >= 20 || new Date().getHours() < 10}
                className="checkbox opacity-0 absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
              />
              <label
                htmlFor="expressDelivery"
                className={`toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                  expressDelivery ? 'bg-green-500' : ''
                }`}
              >
                <span
                  className={`dot absolute left-1 top-1/2 -translate-y-1/4 bg-white w-4 h-4 rounded-full transition-transform duration-300 ease-in-out ${
                    expressDelivery ? 'transform translate-x-4' : ''
                  }`}
                ></span>
              </label>
            </div>
            <label htmlFor="expressDelivery" className="text-sm cursor-pointer text-gray-700">
              Express Delivery (within 1 hour)
            </label>
          </div>

          <div className="flex items-center">
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input
                type="checkbox"
                id="selfPickup"
                checked={selfPickup}
                onChange={(e) => {
                  if (e.target.checked) {
                    setExpressDelivery(false);
                  }
                  setSelfPickup(e.target.checked);
                }}
                className="checkbox opacity-0 absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
              />
              <label
                htmlFor="selfPickup"
                className={`toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                  selfPickup ? 'bg-green-500' : ''
                }`}
              >
                <span
                  className={`dot absolute left-1 top-1/2 -translate-y-1/4 bg-white w-4 h-4 rounded-full transition-transform duration-300 ease-in-out ${
                    selfPickup ? 'transform translate-x-4' : ''
                  }`}
                ></span>
              </label>
            </div>
            <label htmlFor="selfPickup" className="text-sm cursor-pointer text-gray-700">
              Self Pickup
            </label>
          </div>
        </div>

        {/* Today/Tomorrow Selection */}
        <div className="flex rounded-lg overflow-hidden border">
          <button
            className={`flex-1 py-2.5 transition-colors font-medium text-sm ${
              expressDelivery
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : selectedDay === 'today'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-green-50'
            }`}
            onClick={() => !expressDelivery && setSelectedDay('today')}
            disabled={expressDelivery}
          >
            Today
          </button>
          <button
            className={`flex-1 py-2.5 transition-colors font-medium text-sm ${
              expressDelivery
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : selectedDay === 'tomorrow'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-green-50'
            }`}
            onClick={() => !expressDelivery && setSelectedDay('tomorrow')}
            disabled={expressDelivery}
          >
            Tomorrow
          </button>
        </div>

        {/* Time Slots */}
        <div className="grid grid-cols-2 gap-2">
          {timeSlots.map((slot) => {
            const [start, end] = slot.split('-');
            const displaySlot = `${formatTime(start)} - ${formatTime(end)}`;
            const isAvailable = availableSlots.includes(slot);

            return (
              <button
                key={slot}
                className={`py-2 px-1 rounded-lg transition-all text-sm ${
                  expressDelivery
                    ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                    : selectedTimeSlot === slot
                      ? 'bg-green-100 text-green-800 border-2 border-green-500'
                      : isAvailable
                        ? 'bg-white border border-gray-200 text-gray-700 hover:border-green-300 hover:bg-green-50'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                onClick={() => !expressDelivery && isAvailable && setSelectedTimeSlot(slot)}
                disabled={!isAvailable || expressDelivery}
              >
                <Clock className="w-4 h-4 inline-block mr-1" />
                {displaySlot}
              </button>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .toggle-label {
          transition: background-color 0.2s ease;
        }
        .checkbox:checked + .toggle-label {
          background-color: #10b981;
        }
      `}</style>
    </motion.div>
  );
};

export default DeliveryTime;
